import React, { useState } from "react";
import { Search, Plus, Upload, Download, Trash2, Edit, Check, RefreshCcw } from "lucide-react";

export default function Categories() {
  const [search, setSearch] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("All");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);

  const categories = [
    { name: "Computers", published: true, order: 1 },
    { name: "Computers >> Desktops", published: true, order: 1 },
    { name: "Computers >> Desktops >> Electronics", published: true, order: 0 },
    { name: "Computers >> Notebooks", published: true, order: 2 },
    { name: "Computers >> Software", published: true, order: 3 },
    { name: "Electronics", published: true, order: 2 },
    { name: "Electronics >> Camera & photo", published: true, order: 1 },
    { name: "Electronics >> Cell phones", published: true, order: 2 },
    { name: "Electronics >> Others", published: true, order: 3 },
    { name: "Apparel", published: true, order: 3 },
    { name: "Apparel >> Shoes", published: true, order: 1 },
    { name: "Apparel >> Clothing", published: true, order: 2 },
    { name: "Apparel >> Accessories", published: true, order: 3 },
    { name: "Digital downloads", published: true, order: 4 },
    { name: "Books", published: true, order: 5 },
    { name: "Home & Kitchen", published: true, order: 6 },
    { name: "Sports", published: false, order: 7 },
  ];

  // Filtering logic
  const filtered = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) &&
      (publishedFilter === "All" ||
        (publishedFilter === "Published" && c.published) ||
        (publishedFilter === "Unpublished" && !c.published))
  );

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h1 className="text-xl font-semibold text-gray-800 mb-2 sm:mb-0">Categories</h1>
        <div className="flex flex-wrap gap-2">
          <button className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm">
            <Plus size={16} /> Add new
          </button>
          <button className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm">
            <Download size={16} /> Export
          </button>
          <button className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-sm">
            <Upload size={16} /> Import
          </button>
          <button className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm">
            <Trash2 size={16} /> Delete (selected)
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-md border shadow-sm mb-4 p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Category name
            </label>
            <input
              type="text"
              placeholder="Search category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Published
            </label>
            <select
              value={publishedFilter}
              onChange={(e) => setPublishedFilter(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
            >
              <option>All</option>
              <option>Published</option>
              <option>Unpublished</option>
            </select>
          </div>

          <button className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm mt-2 md:mt-0">
            <Search size={16} className="mr-1" /> Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-left">
              <th className="px-4 py-2 border">
                <input type="checkbox" />
              </th>
              <th className="px-4 py-2 border">Name</th>
              <th className="px-4 py-2 border">Published</th>
              <th className="px-4 py-2 border">Display order</th>
              <th className="px-4 py-2 border">Edit</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((cat, i) => (
              <tr
                key={i}
                className={`border-t hover:bg-gray-50 ${
                  i % 2 === 1 ? "bg-gray-50" : ""
                }`}
              >
                <td className="px-4 py-2 border">
                  <input type="checkbox" />
                </td>
                <td className="px-4 py-2 border text-gray-800">{cat.name}</td>
                <td className="px-4 py-2 border text-center">
                  {cat.published && (
                    <Check size={18} className="text-blue-600 inline" />
                  )}
                </td>
                <td className="px-4 py-2 border text-center">{cat.order}</td>
                <td className="px-4 py-2 border text-center">
                  <button className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs mx-auto">
                    <Edit size={14} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer Controls */}
        <div className="flex flex-col md:flex-row items-center justify-between p-3 gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
            >
              {[5, 10, 15, 20, 25].map((num) => (
                <option key={num}>{num}</option>
              ))}
            </select>
            <span>items</span>
          </div>

          <div className="text-sm text-gray-600">
            {`${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
              currentPage * itemsPerPage,
              filtered.length
            )} of ${filtered.length} items`}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded text-sm ${
                currentPage === 1
                  ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded text-sm ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-3 py-1 border rounded text-sm ${
                currentPage === totalPages
                  ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
            >
              Next
            </button>

            <button
              onClick={() => window.location.reload()}
              className="ml-2 p-2 border rounded hover:bg-gray-100"
            >
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3 text-xs text-gray-500 text-center">
        Responsive: 📱 Mobile • 💻 Desktop • 🖥️ TV supported
      </div>
    </div>
  );
}
